/**
 * startingBlockViewModel — la conversation de checkpoint (`checkpointConversationMachine`) en props
 * de `<StudentStartingBlockPage>` : le fil sans jour ni heure, les réponses de Sam, la réponse touchée,
 * le pétale, la fin, l'avis, l'erreur.
 *
 * Pur : les gestes (répondre, écrire, envoyer, l'avis, la croix, « C'est parti ») sont au crochet du
 * moteur, l'écran les branche.
 */
import {
    type CheckpointConversationState,
    type CheckpointReplyAction,
} from "../../../../../engine/precall/checkpoint/checkpointConversationMachine"
import { groupConversation } from "../../../ui/formatters/conversationFormatter"
import { type UICorrectionFeedback } from "../../../ui/types/studentActivity"
import { type UICheckpointReply } from "../../../ui/types/studentCheckpoint"
import { type UIConversationDay } from "../../../ui/types/studentConversation"

export type StartingBlockVM = {
    days: UIConversationDay[]
    thinking: boolean
    replies: UICheckpointReply[]
    chosenReplyKey: string | null
    /** Une réponse demande d'écrire : le composeur s'ouvre. */
    writing: boolean
    textValue: string
    answered: number
    earned: boolean
    ended: boolean
    feedback: UICorrectionFeedback | null
    feedbackSubmitting: boolean
    error: boolean
}

const REPLY_ACTION: Record<CheckpointReplyAction, UICheckpointReply["action"]> = {
    photo: "photo",
    activity: "activity",
    text: "text",
    text_homework: "text",
    date: "date",
}

export function startingBlockViewModel(state: CheckpointConversationState): StartingBlockVM {
    const days = groupConversation(
        state.messages.map((message) => ({
            id: message.id,
            author: message.author === "sam" ? "other" : "self",
            text: message.text,
            attachment: message.imageUri != null ? { kind: "image", uri: message.imageUri } : undefined,
        })),
        { dated: false }
    )
    return {
        days,
        thinking: state.phase === "opening" || state.phase === "sending",
        replies: state.replies.map((reply) => ({
            key: reply.key,
            label: reply.label,
            action: reply.action != null ? REPLY_ACTION[reply.action] : undefined,
        })),
        chosenReplyKey: state.chosenKey,
        writing: state.gesture === "text",
        textValue: state.textValue,
        answered: state.answered,
        earned: state.earned,
        ended: state.phase === "ended",
        feedback: state.feedback === 1 ? "positive" : state.feedback === -1 ? "negative" : null,
        feedbackSubmitting: state.feedbackSending,
        error: state.phase === "error",
    }
}
